import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { formatDate, formatFee } from '../../utils/format';
import { BOOKING_STATUS_LABELS, BOOKING_STATUS_COLORS } from '../../utils/bookingStatus';
import { useToast } from '../../contexts/ToastContext';
import ArtistProfileForm from '../../components/ArtistProfileForm';
import AvailabilityManager from '../../components/AvailabilityManager';
import DashboardSkeleton from '../../components/DashboardSkeleton';

export default function ArtistDashboard() {
  const { addToast } = useToast();
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [bookerInvitations, setBookerInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [acceptingBookingId, setAcceptingBookingId] = useState(null);
  const [acceptFormData, setAcceptFormData] = useState({});

  useEffect(() => {
    async function fetchData() {
      try {
        const [profileRes, bookingsRes, invitationsRes] = await Promise.allSettled([
          api.get('/artists/profile/me'),
          api.get('/bookings/artist/mine'),
          api.get('/bookers/invitations/mine'),
        ]);
        if (profileRes.status === 'fulfilled') setProfile(profileRes.value.data.profile || profileRes.value.data);
        if (bookingsRes.status === 'fulfilled') setBookings(bookingsRes.value.data.bookings || []);
        if (invitationsRes.status === 'fulfilled') setBookerInvitations(invitationsRes.value.data.invitations || []);
      } catch {
        // Erreurs gérées individuellement
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  async function handleProfileCreated() {
    try {
      const res = await api.get('/artists/profile/me');
      setProfile(res.data.profile || res.data);
      setEditing(false);
      addToast('Profil créé avec succès');
    } catch (err) {
      addToast(err.message, 'error');
    }
  }

  async function handleProfileUpdated() {
    try {
      const res = await api.get('/artists/profile/me');
      setProfile(res.data.profile || res.data);
      setEditing(false);
      addToast('Profil mis à jour');
    } catch (err) {
      addToast(err.message, 'error');
    }
  }

  async function handleBookingAction(bookingId, action, acceptedFee = null) {
    try {
      const payload = {};
      if (action === 'accept' && acceptedFee !== null) {
        payload.acceptedFee = Math.round(parseFloat(acceptedFee) * 100);
      }
      const res = await api.patch(`/bookings/${bookingId}/${action}`, payload);
      // Statut réel renvoyé par l'API : 'accepted' ou 'counter_offer' (cachet différent)
      const updated = res.data.booking;
      setBookings((prev) =>
        prev.map((b) => (b.id === bookingId ? { ...b, ...updated } : b))
      );
      setAcceptingBookingId(null);
      setAcceptFormData({});
      addToast(
        action === 'reject'
          ? 'Booking refusé'
          : updated.status === 'counter_offer'
            ? 'Contre-proposition envoyée'
            : 'Booking accepté'
      );
    } catch (err) {
      addToast(err.message, 'error');
    }
  }

  async function handleInvitationAction(invitationId, action) {
    try {
      await api.patch(`/bookers/invitations/${invitationId}/${action}`);
      setBookerInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));
      addToast(action === 'accept' ? 'Invitation acceptée' : 'Invitation refusée');
    } catch (err) {
      addToast(err.message, 'error');
    }
  }

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 animate-fadeIn">
      <h1 className="th-page-title mb-8">Dashboard Artiste</h1>

      {/* Profil artiste */}
      <section className="border th-border p-6 mb-8 th-bg-surface transition-colors duration-300">
        <h2 className="th-section-title mb-4">Mon Profil</h2>
        {!profile ? (
          <ArtistProfileForm mode="create" onSuccess={handleProfileCreated} />
        ) : editing ? (
          <ArtistProfileForm mode="edit" initialData={profile} onSuccess={handleProfileUpdated} />
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {profile.profilePhotoUrl ? (
                  <img src={profile.profilePhotoUrl} alt={profile.artistName} className="w-16 h-16 object-cover grayscale hover:grayscale-0 transition-all duration-150" />
                ) : (
                  <div className="w-16 h-16 dark:bg-dark-elevated bg-light-elevated text-accent flex items-center justify-center text-xl font-mono font-bold">
                    {profile.artistName?.charAt(0)?.toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="font-mono font-semibold th-text">{profile.artistName}</p>
                  <p className="text-sm th-text-muted font-mono">{profile.location?.city ? `${profile.location.city}, ${profile.location.country}` : 'Pas de localisation'}</p>
                  <Link to={`/artists/${profile.slug}`} className="text-sm text-accent hover:opacity-70 font-mono transition-opacity duration-150">
                    Voir profil public →
                  </Link>
                </div>
              </div>
              <button
                onClick={() => setEditing(true)}
                className="th-btn"
              >
                Modifier
              </button>
            </div>
          </>
        )}
      </section>

      {/* Disponibilités */}
      {profile && (
        <section className="border th-border p-6 mb-8 th-bg-surface transition-colors duration-300">
          <h2 className="th-section-title mb-4">Mes Disponibilités</h2>
          <AvailabilityManager />
        </section>
      )}

      {/* Invitations Booker */}
      <section className="mb-8">
        <h2 className="th-section-title mb-4">Invitations Booker</h2>
        {bookerInvitations.length === 0 ? (
          <p className="th-text-muted text-sm font-mono">Aucune invitation de booker pour le moment.</p>
        ) : (
          <div className="space-y-px dark:bg-dark-border bg-light-border">
            {bookerInvitations.map((invitation) => (
              <div key={invitation.id} className="th-bg p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="font-mono font-medium th-text text-sm">{invitation.bookerCompanyName || 'Booker'}</p>
                  <p className="text-xs th-text-muted font-mono mt-0.5">{formatDate(invitation.createdAt)}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (confirm(`Accepter l'invitation de ${invitation.bookerCompanyName || 'ce booker'} ? Il pourra gérer vos bookings.`)) {
                        handleInvitationAction(invitation.id, 'accept');
                      }
                    }}
                    className="th-btn-accent active:opacity-70 transition-opacity duration-100"
                  >
                    Accepter
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Refuser cette invitation ? Cette action est irréversible.')) {
                        handleInvitationAction(invitation.id, 'reject');
                      }
                    }}
                    className="border border-status-rejected/30 text-status-rejected text-xs font-mono uppercase tracking-widest px-3 py-1.5 hover:bg-status-rejected/10 transition-colors duration-150 active:opacity-70"
                  >
                    Refuser
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Bookings reçus */}
      <section>
        <h2 className="th-section-title mb-4">Mes Bookings</h2>
        {bookings.length === 0 ? (
          <div className="border th-border p-6 text-center th-text-secondary text-sm th-bg-surface">
            Aucun booking reçu pour le moment.
          </div>
        ) : (
          <div className="space-y-px dark:bg-dark-border bg-light-border">
            {bookings.map((booking) => (
              <div key={booking.id}>
                {acceptingBookingId === booking.id ? (
                  <div className="th-bg p-5 space-y-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-mono font-medium th-text text-sm">{booking.eventTitle || 'Événement'}</p>
                        <Link
                          to={`/bookings/${booking.id}?tab=messages`}
                          className="text-[10px] font-mono text-accent hover:opacity-70 transition-opacity duration-150"
                        >
                          Messages →
                        </Link>
                      </div>
                      <p className="text-xs th-text-muted font-mono mb-2">
                        {booking.promoterCompanyName || 'Promoteur'} · {formatDate(booking.eventDate)}
                      </p>
                      {booking.proposedFee && (
                        <p className="text-xs th-text-secondary font-mono">
                          Cachet proposé : {formatFee(booking.proposedFee, booking.currency)}
                        </p>
                      )}
                      <div className="flex gap-3 mt-1 mb-3">
                        {booking.travelIncluded && (
                          <span className="text-xs th-text-secondary font-mono">✓ Voyage inclus</span>
                        )}
                        {booking.accommodationIncluded && (
                          <span className="text-xs th-text-secondary font-mono">✓ Hébergement inclus</span>
                        )}
                      </div>
                      {booking.message && (
                        <p className="text-xs th-text-secondary mb-4 italic font-sans">"{booking.message}"</p>
                      )}
                      <label className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
                        Cachet souhaité (optionnel)
                      </label>
                      <input
                        type="number"
                        value={acceptFormData.acceptedFee || ''}
                        onChange={(e) => setAcceptFormData({ acceptedFee: e.target.value })}
                        min="0"
                        step="100"
                        placeholder="Laisser vide pour accepter le montant proposé"
                        className="th-input w-full mb-4"
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleBookingAction(booking.id, 'accept', acceptFormData.acceptedFee || null)}
                        className="th-btn-accent flex-1"
                      >
                        Accepter
                      </button>
                      <button
                        onClick={() => {
                          setAcceptingBookingId(null);
                          setAcceptFormData({});
                        }}
                        className="th-btn flex-1"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="th-bg p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-mono font-medium th-text text-sm">{booking.eventTitle || 'Événement'}</p>
                        <Link
                          to={`/bookings/${booking.id}?tab=messages`}
                          className="text-[10px] font-mono text-accent hover:opacity-70 transition-opacity duration-150"
                        >
                          Messages →
                        </Link>
                      </div>
                      <p className="text-xs th-text-muted font-mono mt-0.5">
                        {booking.promoterCompanyName || 'Promoteur'} · {formatDate(booking.eventDate)}
                      </p>
                      {booking.proposedFee && (
                        <p className="text-xs th-text-secondary font-mono mt-1">
                          Cachet proposé : {formatFee(booking.proposedFee, booking.currency)}
                        </p>
                      )}
                      {booking.status === 'counter_offer' && booking.acceptedFee != null && (
                        <p className="text-xs text-status-pending font-mono mt-1">
                          Contre-proposition envoyée : {formatFee(booking.acceptedFee, booking.currency)} (en attente du promoteur)
                        </p>
                      )}
                      {booking.travelIncluded && (
                        <p className="text-xs th-text-secondary font-mono mt-1">✓ Voyage inclus</p>
                      )}
                      {booking.accommodationIncluded && (
                        <p className="text-xs th-text-secondary font-mono">✓ Hébergement inclus</p>
                      )}
                      {booking.technicalRequirements && (
                        <div className="mt-2 p-2 th-bg-elevated border th-border">
                          <p className="text-[10px] font-mono th-text-muted uppercase tracking-widest mb-1">Rider</p>
                          <p className="text-xs th-text-secondary whitespace-pre-wrap">{booking.technicalRequirements}</p>
                        </div>
                      )}
                      {booking.message && (
                        <p className="text-xs th-text-secondary mt-1 italic font-sans">"{booking.message}"</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className={`th-badge ${BOOKING_STATUS_COLORS[booking.status]}`}>
                        {BOOKING_STATUS_LABELS[booking.status]}
                      </span>
                      {booking.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setAcceptingBookingId(booking.id);
                              setAcceptFormData({});
                            }}
                            className="th-btn-accent active:opacity-70 transition-opacity duration-100"
                          >
                            Accepter
                          </button>
                          <button
                            onClick={() => {
                              if (confirm('Êtes-vous sûr de vouloir refuser ce booking ? Cette action est irréversible.')) {
                                handleBookingAction(booking.id, 'reject');
                              }
                            }}
                            className="border border-status-rejected/30 text-status-rejected text-xs font-mono uppercase tracking-widest px-3 py-1.5 hover:bg-status-rejected/10 transition-colors duration-150 active:opacity-70"
                          >
                            Refuser
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
